import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, ShoppingCart, Star, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { mockCategories } from '../mock';
import { fetchKits } from '../api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const WHATSAPP_NUMBER = '918279765078';

const Medicines = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [cart, setCart] = useState([]);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKits()
      .then((data) => setKits(data))
      .catch(() => setKits([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredMedicines = useMemo(() => {
    let filtered = [...kits];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(medicine =>
        medicine.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        medicine.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        medicine.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(medicine => medicine.category === selectedCategory);
    }

    // Price range filter
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      filtered = filtered.filter(medicine => {
        if (max) {
          return medicine.price >= min && medicine.price <= max;
        }
        return medicine.price >= min;
      });
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    return filtered;
  }, [kits, searchQuery, selectedCategory, priceRange, sortBy]);

  const addToCart = (medicine) => {
    setCart([...cart, medicine.id]);
    // In real app, this would update global cart state
  };

  const orderOnWhatsApp = (medicine) => {
    const message = encodeURIComponent(
      `🌿 *Order Request - Aatreya Herbals*\n\n` +
      `I want to order:\n` +
      `📦 *${medicine.name}*\n` +
      `💰 Price: ₹${medicine.price}\n\n` +
      `Please share the ordering details.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl sm:text-6xl font-bold font-serif">
              Ayurvedic Health Kits
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover complete health solutions - carefully curated kits for specific conditions with proven results
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl border border-border p-6 mb-8 shadow-lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search health kits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {mockCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Range */}
            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger>
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="0-300">Under ₹300</SelectItem>
                <SelectItem value="300-500">₹300 - ₹500</SelectItem>
                <SelectItem value="500-1000">₹500 - ₹1000</SelectItem>
                <SelectItem value="1000">Above ₹1000</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A to Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {filteredMedicines.length} of {kits.length} products
            </p>
            {(searchQuery || selectedCategory !== 'all' || priceRange !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setPriceRange('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </motion.div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-16" data-testid="kits-loading">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading health kits...</p>
          </div>
        ) : filteredMedicines.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMedicines.map((medicine, index) => (
              <motion.div
                key={medicine.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -8 }}
                className="group"
              >
                <div className="bg-card rounded-2xl overflow-hidden border border-border shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                  <div
                    onClick={() => orderOnWhatsApp(medicine)}
                    className="block cursor-pointer"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <motion.img
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        src={medicine.image}
                        alt={`${medicine.name} - Ayurvedic ${medicine.category} kit by Aatreya Herbals`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-medium">
                        {medicine.category}
                      </div>
                      {!medicine.inStock && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                            Out of Stock
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-6 space-y-3 flex-1 flex flex-col">
                    <h3
                      onClick={() => orderOnWhatsApp(medicine)}
                      className="font-serif font-semibold text-xl group-hover:text-primary transition-colors cursor-pointer"
                    >
                      {medicine.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                      {medicine.description}
                    </p>

                    <div className="text-xs text-muted-foreground">
                      📦 {medicine.productsIncluded?.length || 4} products
                    </div>

                    <div className="flex flex-wrap gap-1 mb-2">
                      {medicine.benefits.slice(0, 2).map((benefit, i) => (
                        <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                          {benefit}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <div className="flex flex-col">
                        {medicine.originalPrice && (
                          <span className="text-sm text-muted-foreground line-through">₹{medicine.originalPrice}</span>
                        )}
                        <span className="text-2xl font-bold text-primary">₹{medicine.price}</span>
                      </div>
                      <Button
                        size="sm"
                        disabled={!medicine.inStock}
                        onClick={() => addToCart(medicine)}
                        className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <Button
                      disabled={!medicine.inStock}
                      onClick={() => orderOnWhatsApp(medicine)}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                      data-testid={`whatsapp-order-${medicine.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Order on WhatsApp
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Filter className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-2xl font-serif font-semibold mb-2">No medicines found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or search query
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPriceRange('all');
              }}
            >
              Clear All Filters
            </Button>
          </motion.div>
        )}

        {/* Categories Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mt-16 bg-primary/5 rounded-2xl p-8"
        >
          <h2 className="text-3xl font-serif font-bold mb-6 text-center">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {mockCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.name)}
                className={`p-4 rounded-xl border transition-all hover:shadow-lg ${
                  selectedCategory === category.name
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border hover:border-primary'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">{category.count}</div>
                  <div className="text-sm font-medium">{category.name}</div>
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Medicines;
